import type { FC } from 'react';
import { useCallback, useContext, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { v4 as uuidv4 } from 'uuid';

import { Button } from '@pulse-web-ui/button';
import { Info } from '@pulse-web-ui/icons';
import { SelectorCard } from '@pulse-web-ui/selector-card';

import {
  AdaptiveListWrapper,
  Container,
  FormLabel,
  FormSub,
  LinkContainer,
  RiskWrapper,
} from '@src/components';
import { sendAnalyticEvent } from '@src/components/web-analytic/utils';
import { HouseSteps, analyticEvents } from '@src/constants';
import {
  useCustomerEventRegister,
  useHandlePressKey,
  useNextStep,
} from '@src/hooks';
import { IFLHouseActionTypes, Store } from '@src/store';
import { WizardActionTypes } from '@src/store/wizard';
import { KeyCode } from '@src/types';
import {
  addTestAttribute,
  evenRisk,
  getMainRiskCodesToString,
  sendHouseProgress,
} from '@src/utils';

import { useIflHouseDraft } from './hooks';

export const FormInsuranceAdditionalRisks: FC = () => {
  const { t } = useTranslation();
  const {
    state: {
      stateFormIFLHouse: { risks, dadaValue },
      stateUser: { preset },
    },
    dispatch,
  } = useContext(Store);
  const navigate = useNavigate();

  const { refetch: refetchCustomerEventRegister } = useCustomerEventRegister({
    eventCode: analyticEvents.iflHouseStep3Risk,
    contextData: {
      address: dadaValue?.value,
      risks: getMainRiskCodesToString(risks),
    },
  });

  useEffect(() => {
    if (!preset) {
      sendAnalyticEvent(analyticEvents.iflStep3AddRisk);
      sendAnalyticEvent(analyticEvents.iflHouseStep3Risk);
    }

    refetchCustomerEventRegister();
  }, []);

  const handleKeyPressEnter = () => {
    dispatch({
      type: WizardActionTypes.UpdateWantNextStep,
      payload: true,
    });
  };
  useHandlePressKey(KeyCode.ENTER, handleKeyPressEnter);

  const setStepUpdated = () => {
    dispatch({
      type: WizardActionTypes.SetUpdateFormState,
      payload: true,
    });
  };

  const validatePage = useCallback(() => {
    dispatch({
      type: WizardActionTypes.SetFwNavDisabled,
      payload: !risks?.some(evenRisk) || false,
    });

    return risks?.some(evenRisk) || false;
  }, [risks]);

  useNextStep(validatePage);
  useIflHouseDraft();

  useEffect(() => {
    dispatch({
      type: WizardActionTypes.SetFwNavDisabled,
      payload: false,
    });
  }, []);

  useEffect(() => {
    sendHouseProgress(HouseSteps.ADDITIONAL_RISKS);
  }, []);

  const handleRiskClick = (code: string) => {
    setStepUpdated();

    dispatch({
      type: IFLHouseActionTypes.SetInsuranceSubproducts,
      payload: undefined,
    });

    const newRisks = risks?.map((item) => {
      if (item.code === code) {
        if (preset) {
          sendAnalyticEvent(analyticEvents.iflHousePresetChangeAddRisk);
        } else {
          sendAnalyticEvent(
            item.active
              ? analyticEvents.deselectOptionRealty
              : analyticEvents.selectOptionRealty,
            {
              option: item.name,
            }
          );
        }

        return {
          ...item,
          active: !item.active,
        };
      }
      return item;
    });

    dispatch({
      type: IFLHouseActionTypes.SetRisks,
      payload: newRisks,
    });

    dispatch({
      type: IFLHouseActionTypes.SetInsuranceSubproducts,
      payload: undefined,
    });

    dispatch({
      type: IFLHouseActionTypes.SetGetSubobjectsRefetchUid,
      payload: uuidv4(),
    });

    dispatch({
      type: IFLHouseActionTypes.SetEmptyDependencies,
      payload: [],
    });

    dispatch({
      type: IFLHouseActionTypes.SetFieldWithEmptyDependencies,
      payload: undefined,
    });
  };

  const descriptionNavHandler = () => {
    dispatch({
      type: IFLHouseActionTypes.SetRisksPage,
      payload: 'secondary',
    });
    navigate('/house-risk-descriptions');
  };

  return (
    <Container>
      <FormLabel>{t('COMMON:headers.youChooseWePayAdditional')}</FormLabel>
      <FormSub>{t('COMMON:hints.eventsOccurLessFrequently')}</FormSub>
      <AdaptiveListWrapper>
        {(!risks || risks?.length === 0) && t('COMMON:errors.noData')}
        {risks?.map((item) => {
          return item.binding === true ? (
            <></>
          ) : (
            <RiskWrapper>
              <SelectorCard
                key={item.code}
                id={item.code}
                name={item.code}
                mobileFullWidth
                label={item.name}
                description={item.description}
                checked={item.active}
                readOnly={!item.switchability}
                onClick={() => handleRiskClick(item.code)}
                testId="house-form-additional-risks"
              />
            </RiskWrapper>
          );
        })}
      </AdaptiveListWrapper>
      <LinkContainer>
        <Button
          icon={<Info width={24} color="active" />}
          variant="text"
          onClick={descriptionNavHandler}
          label={t('COMMON:labels.aboutAdditionalRisks') || ''}
          {...addTestAttribute('house-form-button-about-risks')}
        />
      </LinkContainer>
    </Container>
  );
};
